import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { isDefined } from 'remeda';
import { z } from 'zod/v4';
import { addResourceNameToQueryResult, handleZodError, useAxiosInstance } from './utils';
import type { ApiError } from './utils/types';

const CeiSchema1 = z.record(z.string(), z.number().nullable());

const CeiSchema = z.object({
  current_year: CeiSchema1,
  previous_year: CeiSchema1,
});

export type Cei = z.infer<typeof CeiSchema>;

export async function loadCeiFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get('/analytics/ceis');

  const parseResult = CeiSchema.safeParse(data);

  if (!parseResult.success) {
    handleZodError(parseResult, 'someResource');
    throw parseResult.error;
  }

  return parseResult.data;
}

const emptyCei: Cei = {
  previous_year: {},
  current_year: {},
};

export function useLoadCei() {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<Cei, ApiError>({
    queryKey: ['cei'],
    queryFn: () => loadCeiFn(axiosInstance),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return emptyCei;
    },
  });

  return addResourceNameToQueryResult<Cei, ApiError, 'cei', 'PreventUndefined'>(
    'cei',
    queryResult,
    emptyCei,
  );
}
